//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type { PendingSample } from "../../../services/api/domains/pending/pending.types";
import type { useValidationFilters } from "./useValidationFilters";
import type { useValidationSelection } from "./useValidationSelection";

// Dialog Composables
import { useEditBarcodeDialog } from "../../ui/dialogs/useEditBarcodeDialog";
import { useEditResultDialog } from "../../ui/dialogs/useEditResultDialog";
import { useFilterSamplesDialog } from "../../ui/dialogs/useFilterSamplesDialog";
import { useFlagsDialog } from "../../ui/dialogs/useFlagsDialog";
import { usePatientHistoryDialog } from "../../ui/dialogs/usePatientHistoryDialog";
import { useRejectAllDialog } from "../../ui/dialogs/useRejectAllDialog";
import { useRejectResultDialog } from "../../ui/dialogs/useRejectResultDialog";
import { useSampleInfoDialog } from "../../ui/dialogs/useSampleInfoDialog";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Validation Dialogs Composable
 *
 * Centralizes dialog management for validation page.
 *
 * Responsibilities:
 * - Initialize all dialog instances (flags, edit, history, etc.)
 * - Configure dialog callbacks (save, apply, confirm)
 * - Coordinate data refresh after dialog actions
 * - Provide unified dialog props for component binding
 * - Handle barcode edit with cache migration
 * - Handle filter changes with sample refetch
 *
 * Architecture:
 * - One composable for all dialogs (centralized management)
 * - Each dialog is a separate sub-composable instance
 * - Callbacks registered here, invoked by dialog components
 * - Dialog props exposed as computed for reactivity
 *
 * @param key - Unique key for state isolation
 * @param selectionComposable - Selection state
 * @param pendingComposable - Pending data source
 * @param lisComposable - LIS data source
 * @param filtersComposable - Filters state
 * @param fetchSamples - Fetch samples method
 * @param selectSample - Select sample method
 * @param isUpdatingBarcode - Barcode update flag (prevents SignalR conflicts)
 * @param barcodeJustUpdated - Recently updated barcode (for SignalR)
 * @param triggerSilentRefresh - Silent refresh callback
 */
export const useValidationDialogs = (
  key: string,
  selectionComposable: ReturnType<typeof useValidationSelection>,
  pendingComposable: ReturnType<typeof usePending>,
  lisComposable: ReturnType<typeof useLis>,
  filtersComposable: ReturnType<typeof useValidationFilters>,
  fetchSamples: (forceRefresh?: boolean, silent?: boolean) => Promise<void>,
  selectSample: (sample: PendingSample | null, forceRefresh?: boolean) => Promise<void>,
  isUpdatingBarcode: Ref<boolean>,
  barcodeJustUpdated: Ref<string | null>,
  triggerSilentRefresh: () => Promise<void>,
) => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  // Localization
  const { t } = useI18n();

  // Snackbar
  const { showSnackbar } = useSnackbar();

  //
  // ============================================================================
  // DIALOG INSTANCES
  // ============================================================================
  //

  const flagsDialog = useFlagsDialog(`${key}.flagsDialog`);
  const sampleInfoDialog = useSampleInfoDialog(`${key}.sampleInfoDialog`, pendingComposable, lisComposable);
  const editBarcodeDialog = useEditBarcodeDialog(`${key}.editBarcodeDialog`);
  const editResultDialog = useEditResultDialog(`${key}.editResultDialog`);
  const historyDialog = usePatientHistoryDialog(`${key}.historyDialog`, lisComposable);
  const rejectAllDialog = useRejectAllDialog(`${key}.rejectAllDialog`);
  const rejectResultDialog = useRejectResultDialog(`${key}.rejectResultDialog`);
  const filterSamplesDialog = useFilterSamplesDialog(`${key}.filterSamplesDialog`, filtersComposable);

  //
  // ============================================================================
  // DIALOG CALLBACKS
  // ============================================================================
  //

  /**
   * Edit Barcode Dialog - Save Callback
   *
   * Purpose:
   * - Update sample barcode in backend and local cache
   * - Migrate cached data (results/visit) to new barcode
   * - Prevent SignalR race conditions during update
   * - Re-select sample with new barcode after update
   *
   * Responsibilities:
   * - Validate new barcode (non-empty, different from old)
   * - Set flags to prevent SignalR event conflicts
   * - Abort pending API requests for clean state
   * - Call API to update barcode in backend
   * - Migrate cached results and visit to new barcode key
   * - Refetch samples to get fresh metadata
   * - Re-select sample with new barcode
   * - Trigger silent refresh (TEMPORARY workaround)
   * - Show success/error notifications
   * - Clear update flags to re-enable SignalR
   *
   * Flow:
   * 1. Get old and new barcode from dialog state
   * 2. Validate: Non-empty and different
   * 3. Set isUpdatingBarcode flag (prevents SignalR conflicts)
   * 4. Set barcodeJustUpdated for SignalR event filtering
   * 5. Abort all pending API requests
   * 6. Call API: updateSampleBarcode(old, new)
   * 7. If success: Migrate cached data
   *    - Move pending results: moveCachedResults(old, new)
   *    - Move visit data: updateVisitCacheKey(old, new)
   * 8. Refetch samples with forceRefresh + silent flags
   * 9. Close dialog and show success message
   * 10. Find updated sample in refreshed list
   * 11. Re-select sample with new barcode (forceRefresh)
   * 12. Clear barcodeJustUpdated flag
   * 13. Trigger silent refresh (TEMPORARY)
   * 14. Clear isUpdatingBarcode flag (re-enable SignalR)
   */
  editBarcodeDialog.setSave(async () => {
    const sample = editBarcodeDialog.selectedSample.value;
    if (!sample) return;

    const oldBarcode = sample.barcode;
    const newCode = editBarcodeDialog.newBarcode.value.trim();

    // Validate: must have value and be different
    if (!newCode || newCode === oldBarcode) {
      editBarcodeDialog.close();
      return;
    }

    // Set flags to prevent SignalR conflicts
    isUpdatingBarcode.value = true;
    barcodeJustUpdated.value = newCode;

    try {
      editBarcodeDialog.loading.value = true;

      // Abort any pending requests
      pendingComposable.abortAll();
      lisComposable.abortAll();
      await nextTick();

      // Update barcode via API
      const success = await pendingComposable.updateSampleBarcode(oldBarcode, newCode);

      if (success) {
        // Move cached results from old to new barcode
        pendingComposable.moveCachedResults(oldBarcode, newCode);
        lisComposable.updateVisitCacheKey(oldBarcode, newCode);

        // Refetch samples to get fresh metadata from backend
        await pendingComposable.fetchPendingSamples(filtersComposable.apiFilters.value, true, true);

        await nextTick();

        // Close dialog and show success
        editBarcodeDialog.close();
        showSnackbar(t("pages.validation.dialogs.editSample.success"), {
          icon: "mdi-check-circle",
          color: "success",
        });

        // Find updated sample in list
        const updated = pendingComposable.samples.value.find((s) => s.barcode === newCode);
        if (updated) {
          // Re-select sample with new barcode
          // selectionComposable.setSelectedSample(updated);
          await selectSample(updated, true);
          barcodeJustUpdated.value = null;
        }

        // TEMPORARY: Trigger silent refresh (REMOVE WHEN BACKEND FIXED)
        await triggerSilentRefresh();
      } else {
        showSnackbar(t("pages.validation.dialogs.editSample.error"), {
          icon: "mdi-alert-circle",
          color: "error",
        });
      }
    } catch (err) {
      // Error already shown to user via snackbar
      // Log error for debugging without duplicate user notification
      showSnackbar(t("pages.validation.dialogs.editSample.error"), {
        icon: "mdi-alert-circle",
        color: "error",
      });
    } finally {
      editBarcodeDialog.loading.value = false;
      isUpdatingBarcode.value = false;
    }
  });

  /**
   * Filter Samples Dialog - Apply Callback
   *
   * Purpose:
   * - Apply draft filters to active filters
   * - Refetch samples with new filter criteria
   * - Provide immediate visual feedback on filter changes
   *
   * Responsibilities:
   * - Set loading flag to disable apply button
   * - Copy draft filters to applied filters state
   * - Close dialog
   * - Fetch samples with new filters (silent to avoid flicker)
   * - Clear loading flag to re-enable UI
   *
   * Flow:
   * 1. Set filterSamplesDialog.loading = true
   * 2. Apply draft filters: filtersComposable.applyFilters()
   * 3. Close dialog
   * 4. Fetch samples with forceRefresh + silent flags
   * 5. Clear loading flag
   */
  filterSamplesDialog.setApply(async () => {
    filterSamplesDialog.loading.value = true;
    try {
      filtersComposable.applyFilters();
      filterSamplesDialog.close();
      await fetchSamples(true, true);
    } finally {
      filterSamplesDialog.loading.value = false;
    }
  });

  /**
   * Filter Samples Dialog - Clear All Callback
   *
   * Purpose:
   * - Remove all active filters and reset to default view
   * - Fetch complete sample list without filters
   * - Provide quick "show all" functionality
   *
   * Responsibilities:
   * - Clear both draft and applied filters
   * - Close dialog immediately
   * - Fetch samples with no filter criteria
   *
   * Flow:
   * 1. Clear filters: filtersComposable.clearFilters()
   * 2. Close dialog
   * 3. Fetch samples with forceRefresh + silent flags
   *
   * Note: Clears both draft and applied filters to ensure consistency
   */
  filterSamplesDialog.setClearAll(async () => {
    filtersComposable.clearFilters();
    filterSamplesDialog.close();
    await fetchSamples(true, true);
  });

  //
  // ============================================================================
  // COMPUTED PROPS
  // ============================================================================
  //

  // Combined dialog props for ValidationDialogs component
  const dialogProps = computed(() => ({
    flagsDialogProps: flagsDialog.componentProps.value,
    editSampleDialogProps: editBarcodeDialog.componentProps.value,
    sampleInfoDialogProps: sampleInfoDialog.componentProps.value,
    editResultDialogProps: editResultDialog.componentProps.value,
    filterSamplesDialogProps: filterSamplesDialog.componentProps.value,
    patientHistoryDialogProps: historyDialog.componentProps.value,
    confirmRejectAllDialogProps: rejectAllDialog.componentProps.value,
    confirmRejectResultDialogProps: rejectResultDialog.componentProps.value,
  }));

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    // Individual dialogs
    flagsDialog,
    sampleInfoDialog,
    editBarcodeDialog,
    editResultDialog,
    historyDialog,
    rejectAllDialog,
    rejectResultDialog,
    filterSamplesDialog,

    // Computed props
    dialogProps,
  };
};
